import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Plus, Edit, Trash2, BarChart3, Eye, MousePointer,
  Calendar, Users, CheckCircle, AlertCircle, Clock, ArrowRight,
  TrendingUp, Gift, DollarSign, Handshake, ChevronDown, ChevronUp,
  Loader2, MapPin, Activity
} from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { resolveBackendImage } from '../../utils/brandLogo';

// Area labels
const AREA_LABELS = {
  'assistencia_social': 'Assistência',
  'cultura': 'Cultura',
  'esporte': 'Esporte',
  'educacao': 'Educação',
  'saude': 'Saúde',
  'meio_ambiente': 'Meio Ambiente',
  'causa_animal': 'Causa Animal',
  'direitos_humanos': 'Direitos Humanos',
  'empreendedorismo_social': 'Empreendedorismo',
  'outros': 'Outros'
};

// Metric Card
const MetricCard = ({ icon: Icon, label, value, trend, color = 'purple' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
      {trend && (
        <div className="ml-auto flex items-center gap-1 text-green-600 text-sm">
          <TrendingUp className="w-4 h-4" />
          {trend}
        </div>
      )}
    </div>
  </div>
);

// CTA Stats Card
const CTAStatsCard = ({ ctaClicks }) => {
  const ctas = [
    { key: 'donate_items', label: 'Doar Itens', icon: Gift, color: 'orange' },
    { key: 'donate_money', label: 'Doar $', icon: DollarSign, color: 'green' },
    { key: 'volunteer', label: 'Voluntário', icon: Users, color: 'blue' },
    { key: 'participate', label: 'Participar', icon: Calendar, color: 'purple' },
    { key: 'partnership', label: 'Parcerias', icon: Handshake, color: 'teal' }
  ];

  const total = Object.values(ctaClicks || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MousePointer className="w-4 h-4 text-purple-600" />
        Cliques em CTAs
        <span className="ml-auto text-lg font-bold text-purple-600">{total}</span>
      </h4>
      <div className="space-y-2">
        {ctas.map(cta => {
          const count = ctaClicks?.[cta.key] || 0;
          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={cta.key} className="flex items-center gap-2">
              <cta.icon className={`w-4 h-4 text-${cta.color}-500`} />
              <span className="text-sm text-gray-600 flex-1">{cta.label}</span>
              <span className="text-sm font-medium text-gray-900">{count}</span>
              <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-${cta.color}-500 rounded-full`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Organization Card with Dashboard
const OrgCard = ({ org, onDelete, onRefresh }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const loadDashboard = async () => {
    if (dashboard) return;
    setLoadingDashboard(true);
    try {
      const res = await api.get(`/dashboards/org/${org.id}`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) {
      loadDashboard();
    }
    setExpanded(!expanded);
  };

  const getCompletenessColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = () => {
    const status = org.status;
    if (status === 'aprovada' || status === 'aprovado') {
      return (
        <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full" data-testid={`org-status-${org.id}`}>
          <CheckCircle className="w-3 h-3" />
          Aprovada
        </span>
      );
    }
    if (status === 'pendente_analise' || status === 'pendente') {
      return (
        <span className="flex items-center gap-1 bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full" data-testid={`org-status-${org.id}`}>
          <Clock className="w-3 h-3" />
          Em análise
        </span>
      );
    }
    if (status === 'necessita_ajustes') {
      return (
        <span className="flex items-center gap-1 bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full" data-testid={`org-status-${org.id}`}>
          <AlertCircle className="w-3 h-3" />
          Necessita ajustes
        </span>
      );
    }
    if (status === 'suspensa') {
      return (
        <span className="flex items-center gap-1 bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full" data-testid={`org-status-${org.id}`}>
          <AlertCircle className="w-3 h-3" />
          Suspensa
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full" data-testid={`org-status-${org.id}`}>
        <AlertCircle className="w-3 h-3" />
        {status === 'rejeitada' ? 'Rejeitada' : status}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`my-org-card-${org.id}`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-pink-100 to-purple-100 overflow-hidden flex-shrink-0">
            {org.avatar_url ? (
              <img src={resolveBackendImage(org.avatar_url)} alt={org.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-pink-400" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-900">{org.name}</h3>
              {getStatusBadge()}
              {org.verification_status !== 'not_verified' && (
                <span className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  Verificado
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {org.neighborhood}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(org.areas_of_action || []).slice(0, 3).map(area => (
                <span key={area} className="bg-pink-50 text-pink-700 text-xs px-2 py-0.5 rounded-full">
                  {AREA_LABELS[area] || area}
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/social/${org.id}/editar`)}
              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Editar"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate(`/social/${org.id}`)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Ver página"
            >
              <Eye className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status motivo banner */}
        {org.motivo_status && ['rejeitada', 'suspensa', 'necessita_ajustes'].includes(org.status) && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm" data-testid={`org-motivo-${org.id}`}>
            <p className="font-medium text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Retorno da gestão municipal
            </p>
            <p className="text-amber-700 mt-1">{org.motivo_status}</p>
            {['rejeitada', 'necessita_ajustes'].includes(org.status) && (
              <button
                onClick={() => navigate(`/social/${org.id}/editar`)}
                className="mt-2 text-amber-800 font-semibold text-sm flex items-center gap-1 hover:underline"
              >
                Corrigir e reenviar para análise <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {['pendente_analise', 'pendente'].includes(org.status) && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700 flex items-start gap-2" data-testid={`org-pendente-info-${org.id}`}>
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Seu cadastro está em análise pela gestão municipal. Você será notificado por e-mail quando a análise for concluída.</span>
          </div>
        )}

        {/* Completeness Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-500">Completude do perfil</span>
            <span className="font-medium">{org.completeness_score || 0}%</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${getCompletenessColor(org.completeness_score || 0)} rounded-full transition-all`}
              style={{ width: `${org.completeness_score || 0}%` }}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {org.views || org.profile_views || 0} views
          </span>
          <span className="flex items-center gap-1">
            <Activity className="w-4 h-4" />
            {org.active_activities_count || 0} ações ativas
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {org.supporters_count || 0} apoiadores
          </span>
        </div>
      </div>

      {/* Expand Button */}
      <button
        onClick={handleExpand}
        className="w-full py-3 border-t border-gray-100 flex items-center justify-center gap-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
      >
        <BarChart3 className="w-4 h-4" />
        {expanded ? 'Ocultar Dashboard' : 'Ver Dashboard'}
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Dashboard Panel */}
      {expanded && (
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          {loadingDashboard ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : dashboard ? (
            <div className="space-y-4">
              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard
                  icon={Eye}
                  label="Visualizações"
                  value={dashboard.profile_views}
                  color="purple"
                />
                <MetricCard
                  icon={MousePointer}
                  label="Cliques CTAs"
                  value={Object.values(dashboard.cta_clicks || {}).reduce((a, b) => a + b, 0)}
                  color="pink"
                />
                <MetricCard
                  icon={Calendar}
                  label="Ações Ativas"
                  value={dashboard.active_activities}
                  color="blue"
                />
                <MetricCard
                  icon={Users}
                  label="Apoiadores"
                  value={dashboard.supporters_count}
                  color="green"
                />
              </div>

              {/* CTA Breakdown */}
              <CTAStatsCard ctaClicks={dashboard.cta_clicks} />

              {/* Tips */}
              {dashboard.completeness_score < 80 && (
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Dicas para melhorar seu perfil
                  </h4>
                  <ul className="mt-2 space-y-1 text-sm text-yellow-700">
                    {!org.long_description && <li>• Adicione uma descrição detalhada</li>}
                    {!org.avatar_url && <li>• Adicione um logo/avatar</li>}
                    {(!org.gallery || org.gallery.length === 0) && <li>• Adicione fotos à galeria</li>}
                    {!org.impact_metrics?.people_impacted_last_month && <li>• Preencha as métricas de impacto</li>}
                  </ul>
                  <button
                    onClick={() => navigate(`/social/${org.id}/editar`)}
                    className="mt-3 text-yellow-700 font-medium text-sm flex items-center gap-1 hover:underline"
                  >
                    Completar perfil <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">Erro ao carregar dashboard</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function MinhasOrganizacoes() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await api.get('/profile/org-projects');
      setOrgs(res.data);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const handleDelete = async (orgId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta organização?')) return;

    try {
      await api.delete(`/org-projects/${orgId}`);
      setOrgs(orgs.filter(o => o.id !== orgId));
      toast.success('Organização excluída com sucesso');
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir organização');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
          Minhas Organizações
        </h2>
        <button
          onClick={() => navigate('/social/cadastrar-organizacao')}
          className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 rounded-full px-6 py-2.5 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
          data-testid="add-org-button"
        >
          <Plus className="w-4 h-4" />
          Cadastrar Organização
        </button>
      </div>

      {orgs.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-pink-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Nenhuma organização cadastrada</h3>
          <p className="text-gray-500 mb-6">
            Cadastre sua ONG, associação ou projeto social para aparecer na plataforma
          </p>
          <button
            onClick={() => navigate('/social/cadastrar-organizacao')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 rounded-full px-6 py-3 font-semibold transition-all"
          >
            <Plus className="w-4 h-4" />
            Cadastrar Organização
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {orgs.map(org => (
            <OrgCard
              key={org.id}
              org={org}
              onDelete={handleDelete}
              onRefresh={loadOrgs}
            />
          ))}
        </div>
      )}
    </div>
  );
}
